import React, { useMemo } from 'react';

import { TextInput } from '@components/admin/Forms';
import { FormTitle, FormTitleAction } from '@components/admin/FormTitle';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { DevLayout } from '@components/dev/DevLayout';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { SpinnerModal } from '@components/SpinnerModal';
import { TableCellPropsUnion, Table } from '@components/Table/Table';
import { useImpersonate } from '@hooks/mutation/useImpersonate';
import { useUnimpersonate } from '@hooks/mutation/useUnImpersonate';
import { useUserList } from '@hooks/queries/dashboard/useUserList';
import { useUser } from '@hooks/queries/useUser';
import { useSearch } from '@hooks/useSearch';
import { allMemberUserStatus, MemberUser } from '@models/Member';
import { dashboardUsersQuery, UserHelper } from '@models/User';
import { AuthManager } from '@store/AuthManager';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { FormError } from '@utils/errors';
import { createStrings } from '@utils/local';
import { formatDateTime } from '@utils/types';
import { Form, Formik } from 'formik';

const strings = createStrings({
  ja: {
    title: 'ユーザー一覧',
    companyName: '会社名',
    no: 'No.',
    registerTime: '登録時間',
    customerAssigneeName: '顧客側担当者名',
    fullname: '名前',
    cellphone: '電話番号',
    email: 'メール',
    loginStatus: '会員登録',
    from: 'から',
    to: 'まで',
    searchTitle: '絞り込み',
    fromTheNewTime: '時間順 新→古',
    fromTheOldTime: '時間順 古→新',
    seeAll: 'ALL' as const,
    reset: 'リセット',
    impersonate: 'なりすまし',
    unimpersonate: 'なりすましを取り消す',
    action: '操作',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  searchContainer: {
    ...common.flexInline,
    marginTop: 28,
    marginBottom: 28,
  },
  formContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
  },
  searchButton: {
    alignSelf: 'flex-start',
  },
  joinLabel: {
    ...common.normalText,
    fontSize: 14,
    color: theme.palette.grey[700],
    margin: '0 0.5rem',
    paddingTop: 2,
  },
  dataGrid: {
    margin: '1rem 0',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  inputContainer: {
    ...common.flexInline,
    paddingLeft: '0.5rem',
    paddingRight: '0.5rem',
  },
  startContainer: {
    ...common.flexInline,
    alignItems: 'center',
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
  },
  dateInput: {
    borderColor: theme.palette.grey[700],
    borderStyle: 'solid',
    borderWidth: 1,
  },
}));

export const Impersonate: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };
  const { search, updateSearch } = useSearch(dashboardUsersQuery);

  const actions: FormTitleAction[] = [
    {
      title: 'Unimpersonate',
      action: () => {
        unimpersonate();
      },
    },
  ];

  const orderingOptions = [
    {
      value: '-login_at',
      label: strings.fromTheNewTime,
    },
    {
      value: 'login_at',
      label: strings.fromTheOldTime,
    },
  ];

  const memberUserStatusOptions = useMemo(() => {
    return [strings.seeAll, ...allMemberUserStatus].map((status) => ({
      value: status,
      label: status,
    }));
  }, []);

  const { data: userData, isLoading: isUserLoading } = useUser();

  const query = {
    page_size: 50,
    ...search,
  };

  const userListQuery = useUserList(query);
  const { data, isLoading } = userListQuery;

  const {
    mutateAsync: impersonate,
    isLoading: isImpersonateLoading,
    isError: isImpersonateError,
    error: impersonateError,
    reset: impersonateReset,
  } = useImpersonate();

  const {
    mutateAsync: unimpersonate,
    isLoading: isUnimpersonateLoading,
    isError: isUnimpersonateError,
    error: unimpersonateError,
    reset: unimpersonateReset,
  } = useUnimpersonate();

  const headColumns: TableCellPropsUnion[] = [
    {
      label: strings.no,
    },
    {
      label: strings.registerTime,
      type: 'selectable',
      options: orderingOptions,
      onChange: (value: any) => {
        updateSearch({
          page: 0,
          ordering: value,
        });
      },
    },
    {
      label: strings.companyName,
    },
    {
      label: strings.customerAssigneeName,
    },
    {
      label: strings.fullname,
    },
    {
      label: strings.cellphone,
    },
    {
      label: strings.email,
    },
    {
      label: strings.loginStatus,
      type: 'selectable',
      options: memberUserStatusOptions,
      onChange: (value: any) => {
        const status = String(value);
        if (status === 'ALL') {
          updateSearch({ page: 0, login_status: undefined });
        } else {
          updateSearch({
            page: 1,
            login_status: status,
          });
        }
      },
    },
    {
      label: strings.action,
    },
  ];

  function mapItemToTableRowData(index: number, item: MemberUser): TableCellPropsUnion[] {
    return [
      {
        label: String(item.id),
      },
      {
        label: item.date_joined ? formatDateTime(item.date_joined) : '',
      },
      {
        label: item.company_name ?? '',
      },
      {
        label: item.customer_assignee_name ?? '',
      },
      {
        label: item.last_name + ' ' + item.first_name,
      },
      {
        label: item.cellphone ?? '',
      },
      {
        label: item.email ?? '',
      },
      {
        label: item.login_status ?? '',
      },
      {
        label: (
          <div>
            <Button
              onClick={() => {
                impersonate({ id: item.id! });
              }}
              color="primary"
              text="Impersonate"
              size="small"
              shape="round"
              disabled={Boolean(AuthManager.shared.impersonatedUser) || item.id === userData?.id}
            />
          </div>
        ),
      },
    ];
  }

  const bodyRows: TableCellPropsUnion[][] | undefined = data?.results.map((result, index) => {
    return mapItemToTableRowData(index, result);
  });

  return (
    <DevLayout>
      <div className={classes.container}>
        <Alert open={isImpersonateError} severity="error" onClose={impersonateReset}>
          {impersonateError instanceof FormError && impersonateError.getDisplayMessage()}
        </Alert>
        <Alert open={isUnimpersonateError} severity="error" onClose={unimpersonateReset}>
          {unimpersonateError instanceof FormError && unimpersonateError.getDisplayMessage()}
        </Alert>
        <SpinnerModal
          open={isImpersonateLoading || isUnimpersonateLoading || isLoading || isUserLoading}
        />
        <FormTitle title={strings.title} titleSize="large" lineDivider={false} actions={actions} />
        <div>
          <span className={classes.joinLabel}>Current User Name:</span>
          <span className={classes.joinLabel}>
            {userData ? new UserHelper(userData).fullname : '-'}
          </span>
        </div>
        <div>
          <span className={classes.joinLabel}>Impersonated User ID:</span>
          <span className={classes.joinLabel}>
            {AuthManager.shared.impersonatedUser ? AuthManager.shared.impersonatedUser : '-'}
          </span>
        </div>
        <div>
          <span className={classes.joinLabel}>Original User ID:</span>
          <span className={classes.joinLabel}>
            {AuthManager.shared.originalUser ? AuthManager.shared.originalUser : '-'}
          </span>
        </div>
        <div className={classes.searchContainer}>
          <Formik
            initialValues={query}
            enableReinitialize
            onSubmit={async (values) => {
              updateSearch({
                ...values,
                page: 1,
              });
            }}
          >
            {({ resetForm, handleSubmit }) => (
              <Form className={classes.formContainer}>
                <div className={classes.startContainer}>
                  <div className={classes.inputContainer}>
                    <TextInput name="company_name" label={strings.companyName} iconBefore={null} />
                  </div>
                  <div className={classes.inputContainer}>
                    <TextInput
                      name="date_joined_after"
                      label={strings.registerTime}
                      iconBefore={null}
                      type="date"
                    />
                    <span className={classes.joinLabel}>{strings.from}</span>
                    <TextInput name="date_joined_before" noLabel iconBefore={null} type="date" />
                    <span className={classes.joinLabel}>{strings.to}</span>
                  </div>
                </div>
                <div className={classes.startContainer}>
                  <div className={classes.inputContainer}>
                    <TextInput name="email" label={strings.email} iconBefore={null} />
                  </div>
                  <div className={classes.inputContainer}>
                    <Button
                      text={strings.searchTitle}
                      color="primary"
                      type="submit"
                      shape="round"
                      size="small"
                      className={classes.searchButton}
                    />
                  </div>
                  <div className={classes.inputContainer}>
                    <Button
                      text={strings.reset}
                      color="primary"
                      type="button"
                      shape="round"
                      size="small"
                      className={classes.searchButton}
                      onClick={() => {
                        resetForm({
                          values: {
                            page: 1,
                            page_size: 50,
                          },
                        });
                        handleSubmit();
                      }}
                    />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        <Table>
          <Table.Head>
            <Table.Row columns={headColumns} />
          </Table.Head>
          <Table.Body>
            {bodyRows?.map((columns, index) => {
              return <Table.Row key={index} columns={columns} />;
            })}
          </Table.Body>
        </Table>
        <br />
        <div className={classes.paginationContainer}>
          <PaginationWithSearchQuery query={userListQuery} pageSize={query.page_size} />
        </div>
      </div>
    </DevLayout>
  );
};
